import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { scalePages, type ScaleId } from '../data/scalePages';
import { demoUrlFor, getDemoDomain } from '../data/demoCenter';
import { aggregateNote, assessmentResearchStatus, institutionalConditionsNote } from '../data/siteContent';
import { fadeUp } from '../utils/motion';

/** One of NCJI's four proposed application scales. */
export function ScalePage({
  scale
}: {
  scale: ScaleId;
}) {
  const content = scalePages[scale];
  return <>
      <PageHero eyebrow="NCJI · Proposed application scale" title={content.title} lead={content.lead} body={content.intro} backLink={{
      href: '/ncji',
      label: 'NCJI'
    }} data-id="element-3346" data-mp="2ycx3wcomb6mr" />

      <section className="bg-white py-20 lg:py-24" data-id="element-3347" data-mp="wm299o65ddhm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3348" data-mp="wj7k5jc6v4hv">
          {content.sections.map(section => <motion.article key={section.title} {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-16 border-t border-line py-10 first:border-t-0 first:pt-0" data-id="element-3349" data-mp="2s33y2kmo3kfe">
              <h2 className="lg:col-span-5 font-heading font-bold text-2xl lg:text-3xl text-ink-dark tracking-tight leading-tight" data-id="element-3350" data-mp="30ume8vjm608h">
                {section.title}
              </h2>
              <div className="lg:col-span-7 space-y-4" data-id="element-3351" data-mp="293pynn665hhi">
                {section.paragraphs.map(paragraph => <p key={paragraph} className="text-lg text-ink-muted leading-relaxed" data-id="element-3352" data-mp="246g9m8moz7u7">
                    {paragraph}
                  </p>)}
              </div>
            </motion.article>)}
        </div>
      </section>

      <section aria-labelledby="scale-status-title" className="bg-surface-bg border-y border-line py-16 lg:py-20" data-id="element-3353" data-mp="2d8fr121mcdxo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16" data-id="element-3354" data-mp="3oli8s0fgf9jf">
          <h2 id="scale-status-title" className="lg:col-span-5 font-heading font-bold text-3xl text-ink-dark tracking-tight leading-tight" data-id="element-3355" data-mp="2m8n5w6n02l9x">
            Research status and limits
          </h2>
          <div className="lg:col-span-7 space-y-4" data-id="element-3356" data-mp="2v80j9ae8vtzx">
            <p className="text-lg text-ink-dark leading-relaxed" data-id="element-3357" data-mp="2v6flzd0jpwyd">{assessmentResearchStatus}</p>
            {content.aggregate && <p className="text-ink-muted leading-relaxed" data-id="element-3358" data-mp="3058xx106pfwf">{aggregateNote}</p>}
            {content.institutional && <p className="text-ink-muted leading-relaxed" data-id="element-3359" data-mp="27ixqp7i2ogqn">{institutionalConditionsNote}</p>}
          </div>
        </div>
      </section>

      <section aria-labelledby="scale-related-title" className="bg-white py-16 lg:py-20" data-id="element-3360" data-mp="2cgzkamd4jygd">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16" data-id="element-3361" data-mp="2h90rhtvfqyzj">
          <h2 id="scale-related-title" className="lg:col-span-5 font-heading font-bold text-2xl text-ink-dark tracking-tight" data-id="element-3362" data-mp="1jsbqg2eb80r7">
            Related solutions and demos
          </h2>
          <ul className="lg:col-span-7 border-t border-line" data-id="element-3363" data-mp="1h971295x7ex4">
            {content.related.map(item => {
            const domain = item.demo ? getDemoDomain(item.demo) : undefined;
            return <li key={item.label} className="flex flex-wrap items-center justify-between gap-3 border-b border-line py-4" data-id="element-3364" data-mp="179q0251m5p7h">
                  <a href={item.href} className="inline-flex items-center gap-2 font-semibold text-navy hover:text-navy-light transition-colors duration-150" data-id="element-3365" data-mp="138wbd9gck6dj">
                    {domain?.title ?? item.label} <ArrowRight size={16} aria-hidden="true" data-id="element-3366" data-mp="thrx6byhwpjr" />
                  </a>
                  {domain && <a href={demoUrlFor(domain.id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-muted hover:text-navy transition-colors duration-150 whitespace-nowrap" data-id="element-3367" data-mp="oi6w9vzrm74e">
                      {domain.actionLabel} <ArrowUpRight size={14} aria-hidden="true" data-id="element-3368" data-mp="2ao19q99oowbq" />
                      <span className="sr-only" data-id="element-3369" data-mp="2eouwv0fay73k">(opens in a new tab)</span>
                    </a>}
                </li>;
          })}
            <li className="border-b border-line py-4" data-id="element-3370" data-mp="20v3zsrircfoq">
              <a href="/ncji#pillars" className="inline-flex items-center gap-2 font-semibold text-navy hover:text-navy-light transition-colors duration-150" data-id="element-3371" data-mp="25uzvm3hax0t7">
                NCJI’s five pillars <ArrowRight size={16} aria-hidden="true" data-id="element-3372" data-mp="1vydbjgwsuds8" />
              </a>
            </li>
          </ul>
        </div>
      </section>
    </>;
}